/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/connections-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-connections-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/connections-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, user_id, connected_user_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, user_id, connected_user_id, status, created_at, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/connections-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-connections-api-client.ts (API client)
 * - client/api/generated_schemas/connections.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-10-06T17:50:40.171Z
 * Source config: config/entities/connections-entity-config.ts
 * Public fields: 9/9 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConnectionsClient, ConnectionsQueryOptions } from '../generated_clients/generated-connections-api-client.js';
import {
  Connections,
  ConnectionsInsert,
  ConnectionsUpdate,
} from '../../types/generated_schemas/generated-connections-schema.js';

// Field configuration - defined locally to avoid Rollup/Vite bundler issues with 'as const' exports
const ConnectionsFieldConfig = {
  filterable: ['id', 'user_id', 'connected_user_id'] as const,
  sortable: ['created_at', 'updated_at'] as const,
  searchable: [] as const,
  required: ['id', 'user_id', 'connected_user_id', 'status', 'created_at', 'updated_at'] as const,
  readonly: ['id', 'created_at', 'updated_at'] as const,
} as const;

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/connections_business_hooks/

// Query keys with enhanced caching strategy
export const connectionsKeys = {
  all: ['connections'] as const,
  lists: () => [...connectionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...connectionsKeys.lists(), { filters }] as const,
  details: () => [...connectionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...connectionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...connectionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...connectionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...connectionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...connectionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useConnectionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new ConnectionsClient();
};

/**
 * Hook to get all connections with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with connections data
 */
export const useConnectionsList = (options?: ConnectionsQueryOptions) => {
  const client = useConnectionsClient();
  
  return useQuery({
    queryKey: connectionsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single connection by ID
 *
 * @param id - The connection ID
 * @returns React Query result with single connection data
 */
export const useConnections = (id: string) => {
  const client = useConnectionsClient();
  
  return useQuery({
    queryKey: connectionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/connections
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/connections_business_hooks/
 */

/**
 * Hook to create a new connection
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, user_id, connected_user_id, status, created_at, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating connection
 */
export const useCreateConnections = () => {
  const client = useConnectionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: ConnectionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing connection
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating connection
 */
export const useUpdateConnections = () => {
  const client = useConnectionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConnectionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: connectionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: connectionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a connection
 *
 * @returns React Query mutation for deleting connection
 */
export const useDeleteConnections = () => {
  const client = useConnectionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: connectionsKeys.all });
    },
  });
};

/**
 * Hook to count connections with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, user_id, connected_user_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useConnectionsCount = (filter?: Partial<Record<typeof ConnectionsFieldConfig.filterable[number], any>>) => {
  const client = useConnectionsClient();
  
  return useQuery({
    queryKey: connectionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useConnectionsFieldConfig = () => {
  return ConnectionsFieldConfig;
};